import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {caf, ease, easeInOutQuad, easeInQuad} from "./Animate";
import {Badge} from "./UIBlocks";
import {AnimatePickerRotation} from "./UIIcons";
import {funcOrValOr} from "./CommonJS";
import {tailCheck} from "./CommonReact";

export function ToggleDiv({show, className = "", children, dataTest = ""}) {
    const vals = useRef({}).current;
    const rDiv = useRef();
    const setHeight = ht => rDiv.current && (rDiv.current.style.height = ht);

    useLayoutEffect(() => {
        if (vals.lastShow !== undefined)
            setHeight(rDiv.current.scrollHeight + "px");         // set to current scroll height of element: overcome transition limitation of not animating to 'auto'
        if (!show)
            setTimeout(() => setHeight(0));             // begin the anim immediately after expanded height is set to pixels
        else if (vals.lastShow !== undefined)
            rDiv.current.addEventListener("transitionend", () => vals.lastShow && setHeight("auto"), {once: true});  // after Show anim ends, reset height to auto, so window resize will expand as necessary
        vals.lastShow = !!show;
    }, [show]);

    return (
        <div data-test={`${dataTest??""}-sub-menu`} className={tailCheck(className, "overflow-y-hidden", "transition-[height] duration-300")} ref={rDiv}>
            {children}
        </div>
    );
}


export function ToggleDivByHeader({children, show, dispHeader, classNameHeader, classNameContent}) {
    const rDiv = useRef();
    const [isShown, setIsShown] = useState(show);
    const setHeight = ht => rDiv.current && (rDiv.current.style.height = ht);

    function showHide() {
        // set to current scroll height of element: overcome transition limitation of not animating to 'auto'
        setHeight(rDiv.current.scrollHeight + "px");
        if (isShown)        // begin the anim immediately after expanded height is set to pixels
            setTimeout(() => setHeight(0));
        else                // after Show anim ends, reset height to auto, so window resize will expand as necessary
            rDiv.current.addEventListener("transitionend", () => setHeight("auto"), {once: true});
        setIsShown(!isShown);
    }

    useLayoutEffect(() => {
        setIsShown(show);
        setTimeout(() => {
            if (!show)
                setHeight(0);
        });
    }, [children, show]);

    return (
        <div className="flex flex-col">
            <div className={`flex items-center gap-2 cursor-pointer ${isShown} ${classNameHeader}`} onClick={showHide}>
                <AnimatePickerRotation className={`${isShown && "rotate-180"} text-title font-bolder`}/>
                {funcOrValOr(dispHeader, isShown)}
            </div>
            <div className={`relative overflow-y-hidden transition-[height] duration-300 ${classNameContent}`} ref={rDiv}>
                {children}
            </div>
        </div>
    );
}

export function ToggleDivMore({children, show, className, rowsCollapsed = 3}) {
    const vals = useRef({}).current;
    const div = useRef();
    const [isShown, setIsShown] = useState(show);
    const [showBtn, setShowBtn] = useState(true);

    const setHt = ht => div.current.style.height = ht;

    function showHide() {
        // set to current scroll height of element: overcome transition limitation of not animating to 'auto'
        setHt(div.current.scrollHeight + "px");
        if (isShown)        // begin the anim immediately after expanded height is set to pixels
            setTimeout(() => setHt(vals.hCollapse));
        else                // after Show anim ends, reset height to auto, so window resize will expand as necessary
            div.current.addEventListener("transitionend", () => setHt("auto"), {once: true});
        setIsShown(!isShown);
    }

    useLayoutEffect(() => {
        setIsShown(show);
        setShowBtn(true);
        setTimeout(() => {
            const lh = parseInt(window.getComputedStyle(div.current).lineHeight, 10);
            const maxRows = div.current.scrollHeight / lh;
            vals.hCollapse = lh * rowsCollapsed + "px";
            //console.log("div", div.current.scrollHeight, div.current.offsetHeight, lh, maxRows, vals.hCollapse);
            if (maxRows <= rowsCollapsed)       // if expanded height is already within rowsCollapsed, don't show button
                setShowBtn(false);
            else if (!show)
                setHt(vals.hCollapse);
        });
    }, [children, rowsCollapsed, show]);

    return (
        <div className={`relative overflow-y-hidden transition-[height] duration-300 ${className}`} ref={div}>
            {children}
            {showBtn &&
                <Badge pill className="absolute bottom-0 right-0 text-[0.8rem] font-semibold cursor-pointer" onClick={showHide}>
                    {isShown? "less" : "more"}
                </Badge>}
        </div>
    );
}

export function AnimateDivHeight({children, className = undefined}) {
    const vals = useRef({}).current;
    const rDiv = useRef();
    const setStyle = (s, v) => rDiv.current && (rDiv.current.style[s] = v +(v > 0? "px" : ""));
    const setHt = v => setStyle("height", v);

    useLayoutEffect(() => {
        const div = rDiv.current;
        if (div.style.height !== "auto") {  // means transition in progress. calculate estimated current height by comparing time passed
            vals.prevHeight = easeInOutQuad(Date.now() + 50 - (vals.prevTm || Date.now()-300), vals.prevHeight, vals.newHeight - vals.prevHeight, 300) |0;
            setHt("auto");                  // allow to find new target height, while allowing to revert to current animated height
            div.getBoundingClientRect();        // Force a synchronous layout to ensure DOM recalculates element dimensions
        }

        vals.newHeight = div.scrollHeight;
        if (vals.prevHeight === vals.newHeight) return;
        setHt(vals.prevHeight || 0);
        vals.prevTm = Date.now();
        div.getBoundingClientRect();            // Force a synchronous layout to ensure DOM recalculates element dimensions
        setHt(vals.newHeight);
        div.addEventListener("transitionend", () => {
            const ht = div.style.height.slice?.(0, -2) |0;
            if (ht !== vals.newHeight) return;      // new transition started. do nothing here.
            setHt("auto");
            vals.prevHeight = vals.newHeight;
        }, {once: true});
    }, [children]);

    return (
        <div className={`${className || "relative"} shrink-0 min-h-0 overflow-y-hidden overflow-x-visible transition-height duration-300`} ref={rDiv}>
            {children}
        </div>
    );
}

export function AnimateNumber({target, d = undefined, animDur = undefined}) {
    const [cur, setCur] = useState(0);
    const rCur = useRef(0);

    useEffect(() => {
        const oEase = ease({
            start: rCur.current,
            end: target,
            msDur: target > 50? (animDur ?? 700) : 100,
            fEase: easeInQuad,
            onStep: value => setCur(rCur.current = value >> 0),
        });
        return () => oEase.timer && caf(oEase.timer);
    }, [target]);

    return d?.(cur) ?? cur.toLocaleString();
}
